//
//  useFilters.ts
//
//  isana.net,inc. Proprietary and Confidential
//
import { useCallback, useMemo, useState } from "react";

const useFilters = <T, K extends keyof T>(items: T[], filterKey: K) => {
  const [activeFilters, setActiveFilters] = useState<T[K][]>([]);

  const filterList = useMemo(() => {
    return Array.from(new Set(items.map((item) => item[filterKey])));
  }, [items, filterKey]);

  const applyFilters = useCallback(() => {
    if (activeFilters.length === 0) {
      return items;
    }
    return items.filter((item) => activeFilters.includes(item[filterKey]));
  }, [items, activeFilters, filterKey]);

  const handleChecked = useCallback(
    (filterValue: T[K], isAll: boolean = false) => {
      if (isAll) {
        setActiveFilters([]);
        return;
      }
      if (activeFilters.includes(filterValue)) {
        setActiveFilters((prev) =>
          prev.filter((filter) => filter !== filterValue)
        );
      } else {
        setActiveFilters((prev) => [...prev, filterValue]);
      }
    },
    [activeFilters]
  );

  const checkedAll = useMemo(() => activeFilters.length === 0, [activeFilters]);

  return {
    filterList,
    filteredItems: applyFilters(),
    activeFilters,
    checkedAll,
    handleChecked,
  };
};

export default useFilters;
